import { useContext, useEffect, useRef, useState } from "react";
import { Box, Button, TextField, Typography } from "@mui/material";
import { ArrowBack, BackspaceOutlined, Celebration, InstallMobile, WhatsApp } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { QRCodeCanvas } from "qrcode.react";
import jsQR from "jsqr";
import { callPostApi } from "../../function/ApiCallFunction";
import { DataContext } from "../../context/DataContext";
import { cashiLogo } from "../../include/cashiBrand";
import { indiaMinute } from "../../include/indiaDate";
import { primaryButtonSx } from "../admin/consoleTheme";

const fieldSx = {
  "& .MuiInputBase-root": { borderRadius: 2, background: "#fff" },
  "& .MuiInputBase-input": { fontSize: 16, py: 1.35 },
};

function minuteNow() {
  return indiaMinute();
}

function money(value) {
  return Number(value || 0).toFixed(2);
}

function entryText(entry) {
  if (entry.endsWith(".")) return `${entry.slice(0, -1) || "0"}.`;
  if (entry.includes(".")) {
    const [whole, dec] = entry.split(".");
    return `${whole || "0"}.${dec}`;
  }
  return money(entry);
}

export const SALE_TOKEN_KEY = "cashi-sale-token";

function rememberSaleToken(token) {
  if (!token) return;
  try { localStorage.setItem(SALE_TOKEN_KEY, token); } catch { /* private browsing */ }
}

function SpinSale({ storeId }) {
  const [busy, setBusy] = useState(false);
  const armWheel = async () => {
    if (!storeId || busy) return;
    setBusy(true);
    try {
      const spin = await callPostApi("store-spin", { mod: "SPIN_ARM", data_arr: { store_id: storeId } });
      if (spin.status === "200" && spin.data?.armed === "1") toast.success("Spin is ready");
      else toast.info(spin.data?.message || spin.status_message || "No spin wheel is running");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button type="button" aria-label="Start the spin" onClick={armWheel} disabled={busy} style={iconBtn}>
      <Celebration sx={{ fontSize: 22, color: "#fff" }} />
    </button>
  );
}

function installedSaleApp() {
  return window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
}

function iosSalePhone() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

function InstallSale() {
  const [promptEvent, setPromptEvent] = useState(null);
  const [help, setHelp] = useState(false);
  const [installed, setInstalled] = useState(() => installedSaleApp());

  useEffect(() => {
    const onPrompt = (event) => {
      event.preventDefault();
      setPromptEvent(event);
    };
    const onInstalled = () => {
      setInstalled(true);
      setPromptEvent(null);
      setHelp(false);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return <Box sx={{ width: 40 }} />;

  const install = async () => {
    if (!promptEvent) {
      setHelp(true);
      return;
    }
    promptEvent.prompt();
    await promptEvent.userChoice;
    setPromptEvent(null);
  };

  const ios = iosSalePhone();

  return (
    <>
      <button type="button" aria-label="Install app" onClick={install} style={iconBtn}>
        <InstallMobile sx={{ fontSize: 22, color: "#fff" }} />
      </button>
      {help && (
        <Box sx={{ position: "fixed", inset: 0, zIndex: 20, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={() => setHelp(false)}>
          <Box onClick={(event) => event.stopPropagation()} sx={{ width: "100%", maxWidth: 480, background: "#fff", color: "#111", borderRadius: "18px 18px 0 0", px: 2.5, pt: 2.5, pb: "max(22px, env(safe-area-inset-bottom))" }}>
            <Typography sx={{ fontSize: 18, fontWeight: 750, mb: 1 }}>Add Cashi Sale to this phone</Typography>
            {ios ? (
              <Typography sx={{ fontSize: 15, lineHeight: 1.45 }}>
                Tap the Share button in Safari, then tap Add to Home Screen. The icon opens this store’s sale page.
              </Typography>
            ) : (
              <Typography sx={{ fontSize: 15, lineHeight: 1.45 }}>
                Open the Chrome menu and tap Install app, or Add to Home screen. The icon opens this store’s sale page. Chrome shows Install on its own when the site is on https.
              </Typography>
            )}
            <button type="button" onClick={() => setHelp(false)} style={{ ...typeChoice, width: "100%", marginTop: 16 }}>
              OK
            </button>
          </Box>
        </Box>
      )}
    </>
  );
}

export function SaleHome() {
  const navigate = useNavigate();
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    let token = "";
    try { token = localStorage.getItem(SALE_TOKEN_KEY) || ""; } catch { token = ""; }
    if (token) navigate(`/sale/${token}`, { replace: true });
    else setMissing(true);
  }, [navigate]);

  return (
    <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", px: 3, background: "#1b2026", color: "#fff", textAlign: "center" }}>
      <Typography sx={{ fontSize: 16, fontWeight: 650, maxWidth: 320 }}>
        {missing ? "Open the store sale link on this phone once. After that, this app opens that store." : "Opening sales calculator..."}
      </Typography>
    </Box>
  );
}

function couponCount(amount, minSale) {
  const total = Number(amount || 0);
  const min = Number(minSale || 0);
  if (!(total > 0)) return 0;
  if (!(min > 0)) return 1;
  return Math.floor(total / min);
}

function scanCanvas(canvas) {
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const code = jsQR(pixels.data, pixels.width, pixels.height, { inversionAttempts: "attemptBoth" });
  return String(code?.data || "").trim();
}

function scanRegion(bitmap, sx, sy, sw, sh, maxEdge) {
  const scale = Math.min(1, maxEdge / Math.max(sw, sh));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(sw * scale));
  canvas.height = Math.max(1, Math.floor(sh * scale));
  const ctx = canvas.getContext("2d", { willReadFrequently: true });
  ctx.drawImage(bitmap, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);
  return scanCanvas(canvas);
}

async function readQrFile(file) {
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  try {
    const width = bitmap.width;
    const height = bitmap.height;
    const cx = Math.floor(width * 0.15);
    const cy = Math.floor(height * 0.15);
    const regions = [
      [0, 0, width, height],
      [cx, cy, Math.floor(width * 0.7), Math.floor(height * 0.7)],
    ];
    for (const edge of [900, 1400, 500]) {
      for (const region of regions) {
        const found = scanRegion(bitmap, region[0], region[1], region[2], region[3], edge);
        if (found) return found;
      }
    }
    return "";
  } finally {
    bitmap.close();
  }
}

async function openRearCamera() {
  const attempts = [
    { audio: false, video: { facingMode: { exact: "environment" } } },
    { audio: false, video: { facingMode: "environment" } },
    { audio: false, video: true },
  ];
  let lastError;
  for (const constraints of attempts) {
    try {
      return await navigator.mediaDevices.getUserMedia(constraints);
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error("Camera unavailable");
}

function LiveScanner({ onCode, onClose }) {
  const videoRef = useRef(null);
  const onCodeRef = useRef(onCode);
  const [error, setError] = useState("");
  onCodeRef.current = onCode;

  useEffect(() => {
    let stream;
    let timer;
    let stopped = false;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    let detector = null;
    if ("BarcodeDetector" in window) {
      try { detector = new window.BarcodeDetector({ formats: ["qr_code"] }); } catch { detector = null; }
    }

    const stop = () => {
      stopped = true;
      clearTimeout(timer);
      stream?.getTracks().forEach((track) => track.stop());
    };

    const finish = (value) => {
      const code = String(value || "").trim();
      if (!code || stopped) return false;
      stop();
      onCodeRef.current(code);
      return true;
    };

    const tick = async () => {
      if (stopped) return;
      const video = videoRef.current;
      try {
        if (video && video.readyState >= 2 && video.videoWidth) {
          if (detector) {
            const codes = await detector.detect(video);
            if (finish(codes?.[0]?.rawValue)) return;
          }
          const scale = Math.min(1, 900 / Math.max(video.videoWidth, video.videoHeight));
          canvas.width = Math.max(1, Math.floor(video.videoWidth * scale));
          canvas.height = Math.max(1, Math.floor(video.videoHeight * scale));
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          if (finish(scanCanvas(canvas))) return;
        }
      } catch { /* keep scanning */ }
      if (!stopped) timer = setTimeout(tick, 180);
    };

    openRearCamera().then(async (next) => {
      stream = next;
      if (stopped) {
        next.getTracks().forEach((track) => track.stop());
        return;
      }
      const video = videoRef.current;
      if (!video) return;
      video.srcObject = next;
      try { await video.play(); } catch { /* autoPlay starts the preview on the phone */ }
      tick();
    }).catch(() => {
      if (!stopped) setError("Allow the camera, then point it at the coupon QR.");
    });

    return stop;
  }, []);

  return (
    <Box sx={{ position: "fixed", inset: 0, zIndex: 1400, background: "#000", display: "flex", flexDirection: "column" }}>
      <Box sx={{ position: "relative", flex: 1, minHeight: 0 }}>
        <video ref={videoRef} playsInline muted autoPlay style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        <Box sx={{ position: "absolute", left: "12%", right: "12%", top: "16%", bottom: "16%", border: "3px solid #21a7ec", borderRadius: 2, pointerEvents: "none" }} />
      </Box>
      <Box sx={{ px: 2, pt: 1.5, pb: "max(16px, env(safe-area-inset-bottom))", textAlign: "center" }}>
        <Typography sx={{ color: "#fff", fontSize: 15, fontWeight: 650 }}>
          {error || "Point the camera at the coupon QR"}
        </Typography>
        <button type="button" onClick={onClose} style={{ ...typeChoice, marginTop: 12 }}>CLOSE CAMERA</button>
      </Box>
    </Box>
  );
}

function QrScanner({ onCode, onType }) {
  const onCodeRef = useRef(onCode);
  const imageId = useRef(`qr-image-${Math.random().toString(36).slice(2)}`).current;
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState("");
  const [camera, setCamera] = useState(false);
  onCodeRef.current = onCode;

  useEffect(() => () => {
    if (preview) URL.revokeObjectURL(preview);
  }, [preview]);

  const onPhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setBusy(true);
    setNote("Reading the QR code...");
    const url = URL.createObjectURL(file);
    setPreview((previous) => {
      if (previous) URL.revokeObjectURL(previous);
      return url;
    });
    try {
      const value = await readQrFile(file);
      if (!value) {
        setNote("Could not read that photo. Fill the frame with the coupon QR and take it again.");
        return;
      }
      setNote("");
      onCodeRef.current(value);
    } catch (error) {
      setNote("Could not read that photo. Enter the coupon code below.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box>
      {camera && (
        <LiveScanner
          onClose={() => setCamera(false)}
          onCode={(value) => {
            setCamera(false);
            setNote("");
            onCodeRef.current(value);
          }}
        />
      )}
      {preview && (
        <Box component="img" src={preview} alt="Coupon photo" sx={{ width: "100%", maxHeight: 220, objectFit: "contain", borderRadius: 2, background: "#000", display: "block" }} />
      )}
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: 1 }}>
        <Box component="button" type="button" disabled={busy} onClick={() => setCamera(true)} sx={{ ...scanChoice(busy), width: "100%", font: "inherit" }}>
          {busy ? "READING..." : "OPEN CAMERA"}
        </Box>
        <Box component="label" htmlFor={imageId} sx={scanChoice(busy, true)}>
          {busy ? "READING..." : "OPEN IMAGE"}
          <input id={imageId} type="file" accept="image/*" disabled={busy} onChange={onPhoto} style={{ display: "none" }} />
        </Box>
      </Box>
      <button type="button" onClick={onType} disabled={busy} style={typeChoice}>TYPE CODE</button>
      <Typography sx={{ fontSize: 13, color: "#94a3b8", mt: 1, lineHeight: 1.4, textAlign: "center" }}>
        {note || "Open the camera to scan the QR, choose a saved photo, or type the coupon code."}
      </Typography>
    </Box>
  );
}

export default function RegisterSale() {
  const navigate = useNavigate();
  const { token: publicToken = "" } = useParams();
  const publicMode = Boolean(publicToken);
  const { loginData } = useContext(DataContext);
  const [publicStore, setPublicStore] = useState(null);
  const [linkError, setLinkError] = useState("");
  const storeId = publicMode ? publicStore?.store_id : loginData?.store_info?.store_id;
  const storeName = publicMode ? publicStore?.store_name : loginData?.store_info?.store_name;
  const [draws, setDraws] = useState([]);
  const [entry, setEntry] = useState("0");
  const [lines, setLines] = useState([]);
  const [phase, setPhase] = useState("calc");
  const [form, setForm] = useState({ customer_mobile: "", customer_name: "", customer_email: "" });
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [spinNote, setSpinNote] = useState("");
  const [scanDraw, setScanDraw] = useState(null);
  const codeRef = useRef(null);
  const [payOpen, setPayOpen] = useState(false);
  const [usePoints, setUsePoints] = useState(true);
  const [quote, setQuote] = useState(null);
  const [prizeClaim, setPrizeClaim] = useState(null);
  const [claimForm, setClaimForm] = useState({ customer_name: "", customer_mobile: "", customer_email: "" });
  const [claimSaving, setClaimSaving] = useState(false);
  const dismissedClaim = useRef("");
  const filledFromLookup = useRef("");
  const saleRef = useRef({ form: { customer_mobile: "", customer_name: "", customer_email: "" }, result: null });
  saleRef.current = { form, result };

  useEffect(() => {
    if (publicToken) rememberSaleToken(publicToken);
  }, [publicToken]);

  useEffect(() => {
    if (!publicMode) return undefined;
    let mounted = true;
    callPostApi("store-draw", { mod: "SALE_OPEN", data_arr: { token: publicToken } }).then((response) => {
      if (!mounted) return;
      if (response.status === "200" && response.data?.store_id) setPublicStore(response.data);
      else setLinkError(response.data?.error || response.status_message || "This sale link is not valid");
    }).catch(() => {
      if (mounted) setLinkError("This sale link is not valid");
    });
    return () => { mounted = false; };
  }, [publicMode, publicToken]);

  useEffect(() => {
    if (!storeId) return undefined;
    let mounted = true;
    callPostApi("store-draw", { mod: "DRAW_LIST", data_arr: { store_id: storeId } }).then((response) => {
      if (mounted && response.status === "200") setDraws(response.data?.draws || []);
    });
    return () => { mounted = false; };
  }, [storeId]);

  useEffect(() => {
    if (!storeId) return undefined;
    let mounted = true;
    const load = () => {
      callPostApi("store-spin", { mod: "SPIN_PENDING_CLAIM", data_arr: { store_id: storeId } }).then((response) => {
        if (!mounted) return;
        const row = response.data?.claim;
        if (row?.claim_id && row.claim_id !== dismissedClaim.current) {
          setPrizeClaim((current) => {
            if (!current || current.claim_id !== row.claim_id) return row;
            if (row.saved && !current.saved) return { ...current, ...row };
            return current;
          });
        } else if (!row) setPrizeClaim(null);
      });
    };
    load();
    const timer = setInterval(load, 2000);
    return () => { mounted = false; clearInterval(timer); };
  }, [storeId]);

  useEffect(() => {
    if (!prizeClaim?.claim_id) return;
    const sale = saleRef.current;
    setClaimForm({
      customer_name: sale.result?.customer_name || sale.form.customer_name || "",
      customer_mobile: sale.result?.customer_mobile || sale.form.customer_mobile || "",
      customer_email: sale.result?.customer_email || sale.form.customer_email || "",
    });
  }, [prizeClaim?.claim_id]);

  const committed = lines.reduce((sum, value) => sum + value, 0);
  const pendingEntry = Number(entry) > 0 ? Number(entry) : 0;
  const amount = Math.round((committed + pendingEntry) * 100) / 100;
  const minute = minuteNow();
  const matched = draws.filter((draw) => (
    String(draw.status) === "1"
    && draw.start_at
    && draw.end_at
    && draw.start_at <= minute
    && draw.end_at >= minute
    && couponCount(amount, draw.min_sale_amount) > 0
  )).map((draw) => ({ ...draw, coupons: couponCount(amount, draw.min_sale_amount) }));

  useEffect(() => {
    if (phase !== "customer" || !storeId) return undefined;
    const mobile = form.customer_mobile.replace(/\D/g, "");
    if (mobile.length < 10) {
      setQuote(null);
      return undefined;
    }
    let mounted = true;
    callPostApi("store-draw", {
      mod: "SALE_LOYALTY_QUOTE",
      data_arr: { store_id: storeId, customer_mobile: mobile, amount },
    }).then((response) => {
      if (!mounted || response.status !== "200") return;
      const data = response.data || {};
      setQuote(data);
      if (filledFromLookup.current === mobile) return;
      if (data.customer_found) {
        filledFromLookup.current = mobile;
        setForm((prev) => ({
          ...prev,
          customer_name: data.customer_name || prev.customer_name,
          customer_email: data.customer_email || prev.customer_email,
        }));
      } else if (filledFromLookup.current) {
        filledFromLookup.current = "";
        setForm((prev) => ({ ...prev, customer_name: "", customer_email: "" }));
      }
    });
    return () => { mounted = false; };
  }, [phase, storeId, form.customer_mobile, amount]);

  const press = (key) => {
    if (key === "clear") {
      setEntry("0");
      setLines([]);
      return;
    }
    if (key === "back") {
      setEntry((value) => {
        const next = value.slice(0, -1);
        return next === "" ? "0" : next;
      });
      return;
    }
    if (key === "+") {
      const next = Number(entry);
      if (next > 0) {
        setLines((prev) => [...prev, next]);
        setEntry("0");
      }
      return;
    }
    if (key === ".") {
      setEntry((value) => (value.includes(".") ? value : `${value}.`));
      return;
    }
    setEntry((value) => {
      const current = value === "0" ? "" : value;
      const next = `${current}${key}`;
      const decimals = next.split(".")[1];
      if (decimals && decimals.length > 2) return value;
      if (next.replace(".", "").length > 8) return value;
      return next || "0";
    });
  };

  const resetSale = () => {
    setEntry("0");
    setLines([]);
    setForm({ customer_mobile: "", customer_name: "", customer_email: "" });
    setDraft("");
    setResult(null);
    setScanDraw(null);
    setPayOpen(false);
    setUsePoints(true);
    setQuote(null);
    filledFromLookup.current = "";
    setPhase("calc");
  };

  const openCustomer = () => {
    if (amount <= 0) {
      toast.info("Enter the sale amount");
      return;
    }
    setPhase("customer");
  };

  const submit = async (event) => {
    event?.preventDefault();
    const mobile = form.customer_mobile.replace(/\D/g, "");
    if (mobile.length < 10) {
      toast.info("Enter a valid mobile number");
      return;
    }
    if (!form.customer_name.trim()) {
      toast.info("Customer name is required");
      return;
    }
    setSaving(true);
    try {
      const response = await callPostApi("store-draw", {
        mod: "SALE_REGISTER",
        data_arr: {
          store_id: storeId,
          customer_mobile: mobile,
          customer_name: form.customer_name.trim(),
          customer_email: form.customer_email.trim(),
          amount,
          use_points: usePoints ? "1" : "0",
        },
      });
      if (response.status !== "200") {
        toast.info(response.status_message || response.data?.error || "Could not save the sale");
        return;
      }
      const data = response.data || {};
      const pending = (data.pending || []).flatMap((item) => {
        if (item.coupon_index && item.slot_id) return [item];
        const total = Math.max(1, couponCount(data.amount, item.min_sale_amount));
        return Array.from({ length: total }, (_, index) => ({
          ...item,
          slot_id: `${item.draw_id}-${index + 1}`,
          coupon_index: index + 1,
          coupon_total: total,
        }));
      });
      setResult({ ...data, pending });
      setSpinNote("");
      setPayOpen(false);
      setPhase("receipt");
    } finally {
      setSaving(false);
    }
  };

  const savePrizeClaim = async () => {
    if (!prizeClaim?.claim_id || claimSaving) return;
    setClaimSaving(true);
    try {
      const response = await callPostApi("spin-claim", {
        mod: "SPIN_CLAIM_SAVE",
        data_arr: {
          claim_id: prizeClaim.claim_id,
          customer_name: claimForm.customer_name,
          customer_mobile: claimForm.customer_mobile,
          customer_email: claimForm.customer_email,
        },
      });
      if (response.status === "200") {
        setPrizeClaim((current) => (current ? {
          ...current,
          saved: true,
          customer_name: String(claimForm.customer_name || "").trim(),
          customer_mobile: String(claimForm.customer_mobile || "").replace(/\D/g, ""),
        } : current));
      } else toast.info(response.status_message || response.data?.error || "Could not save the prize");
    } finally {
      setClaimSaving(false);
    }
  };

  const updatePayment = async (payment_type) => {
    const response = await callPostApi("store-draw", {
      mod: "SALE_PAYMENT",
      data_arr: { store_id: storeId, sale_id: result?.sale_id, payment_type },
    });
    if (response.status !== "200") {
      toast.info(response.status_message || response.data?.error || "Could not update payment");
      return;
    }
    setResult((prev) => ({ ...prev, payment_type }));
    setPayOpen(false);
    toast.success(response.data?.message || "Payment updated");
  };

  const shareWhatsApp = () => {
    const mobile = String(result?.customer_mobile || "").replace(/\D/g, "");
    const unlocked = (result?.unlocked || []).map((item) => item.offer_title).filter(Boolean).join(", ");
    const text = [
      "CASHI sale",
      `Customer: ${result?.customer_name}`,
      `Amount: ₹${money(result?.amount)}`,
      Number(result?.points_redeemed) ? `Used: ${result.points_redeemed} points${result.redeem_text ? ` (${result.redeem_text})` : ""}` : "",
      Number(result?.points_earned) ? `Loyalty: +${result.points_earned} (balance ${result.loyalty_point})` : "",
      unlocked ? `Coupons: ${unlocked}` : "",
    ].filter(Boolean).join("\n");
    window.open(`https://wa.me/${mobile ? `91${mobile.slice(-10)}` : ""}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  const issuePending = async (rawCode) => {
    if (saving || !scanDraw) return;
    const coupon_code = String(rawCode ?? draft ?? "").trim();
    if (coupon_code.length < 4) {
      toast.info("Scan or enter the coupon code");
      return;
    }
    setSaving(true);
    try {
      const response = await callPostApi("store-draw", {
        mod: "SALE_ISSUE_COUPON",
        data_arr: {
          store_id: storeId,
          draw_id: scanDraw.draw_id,
          sale_id: result?.sale_id,
          coupon_code,
          customer_mobile: result?.customer_mobile,
          customer_name: result?.customer_name,
          customer_email: result?.customer_email || form.customer_email,
          amount: result?.amount,
        },
      });
      if (response.status !== "200") {
        toast.info(response.status_message || response.data?.error || "Could not issue the coupon");
        return;
      }
      const issued = [...(result?.issued || []), { ...response.data, coupon_index: scanDraw.coupon_index, coupon_total: scanDraw.coupon_total }];
      const pending = (result?.pending || []).filter((item) => item.slot_id !== scanDraw.slot_id);
      setResult({ ...result, issued, pending, message: "Coupon issued" });
      setDraft("");
      if (pending.length) setScanDraw(pending[0]);
      else setPhase("celebrate");
    } finally {
      setSaving(false);
    }
  };

  const startNewSale = () => {
    if (prizeClaim?.claim_id) dismissedClaim.current = prizeClaim.claim_id;
    setPrizeClaim(null);
    setPhase("calc");
    setEntry("0");
    setLines([]);
    setForm({ customer_mobile: "", customer_name: "", customer_email: "" });
    setResult(null);
    setSpinNote("");
    setScanDraw(null);
    setDraft("");
    setPayOpen(false);
    setClaimForm({ customer_name: "", customer_mobile: "", customer_email: "" });
  };

  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", ".", "0", "+"];

  if (publicMode && linkError) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", px: 3, background: "#111", color: "#fff", textAlign: "center" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 700 }}>{linkError}</Typography>
      </Box>
    );
  }

  if (publicMode && !storeId) {
    return (
      <Box sx={{ minHeight: "100dvh", display: "grid", placeItems: "center", background: "#1b2026", color: "#fff" }}>
        <Typography sx={{ fontSize: 16 }}>Opening sales calculator...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: publicMode ? "100dvh" : "calc(100dvh - 92px)", background: phase === "calc" ? "#1b2026" : "#111", display: "flex", justifyContent: "center", overflow: "hidden", pb: publicMode ? "env(safe-area-inset-bottom)" : 0 }}>
      <Box sx={{ width: "100%", maxWidth: phase === "calc" ? 480 : "100%", height: "100%", display: "flex", flexDirection: "column", background: phase === "calc" ? "#2c3136" : "transparent", position: "relative", overflow: "hidden" }}>
        {prizeClaim && (
          <Box sx={{ position: "absolute", inset: 0, zIndex: 40, background: "rgba(8,12,20,0.78)", display: "grid", placeItems: "center", px: 2, py: 2 }}>
            <Box sx={{ width: "100%", maxWidth: 400, background: "#fff", borderRadius: 3, overflow: "hidden", boxShadow: "0 18px 40px rgba(0,0,0,0.35)" }}>
              <Box sx={{ background: "linear-gradient(110deg, #0a0a0a, #7c3aed, #db2777)", px: 2, py: 1.2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box component="img" src={cashiLogo} alt="Cashi" sx={{ height: 26, width: "auto" }} />
                <button type="button" onClick={prizeClaim.saved ? startNewSale : () => { dismissedClaim.current = prizeClaim.claim_id; setPrizeClaim(null); }} style={{ ...iconBtn, width: "auto", padding: "4px 10px", fontSize: 13, color: "#fff" }}>Close</button>
              </Box>
              <Box sx={{ p: 2.2, background: prizeClaim.saved ? "linear-gradient(180deg, #fff7ed 0%, #ffffff 42%)" : "#fff" }}>
                <Typography sx={{ textAlign: "center", fontSize: 13, fontWeight: 800, letterSpacing: 1.2, color: "#ea580c", textTransform: "uppercase" }}>{prizeClaim.saved ? "You won" : "Claim prize"}</Typography>
                {prizeClaim.headline ? <Typography sx={{ textAlign: "center", mt: 0.4, color: "#64748b" }}>{prizeClaim.headline}</Typography> : null}
                <Typography sx={{ textAlign: "center", fontSize: 26, fontWeight: 900, color: "#15803d", mt: 0.8, lineHeight: 1.15 }}>{prizeClaim.prize_title}</Typography>
                {prizeClaim.saved ? (
                  <>
                    <Typography sx={{ textAlign: "center", fontSize: 18, fontWeight: 800, color: "#0f172a", mt: 1.4 }}>{prizeClaim.customer_name}</Typography>
                    <Typography sx={{ textAlign: "center", fontSize: 16, fontWeight: 700, color: "#334155", mt: 0.25 }}>{prizeClaim.customer_mobile}</Typography>
                    <Typography sx={{ textAlign: "center", fontSize: 16, fontWeight: 700, color: "#7c3aed", mt: 1.1, mb: 1.6 }}>won this prize</Typography>
                    <Button variant="contained" sx={{ ...primaryButtonSx, minHeight: 46, width: "100%" }} onClick={startNewSale}>Close</Button>
                  </>
                ) : (
                  <>
                    <Typography sx={{ textAlign: "center", mt: 1, mb: 1.6, color: "#334155", fontSize: 14 }}>Enter name and mobile to claim this prize.</Typography>
                    <Box sx={{ display: "grid", gap: 1.2 }}>
                      <TextField label="Name" value={claimForm.customer_name} onChange={(event) => setClaimForm((prev) => ({ ...prev, customer_name: event.target.value }))} />
                      <TextField label="Mobile" value={claimForm.customer_mobile} onChange={(event) => setClaimForm((prev) => ({ ...prev, customer_mobile: event.target.value.replace(/\D/g, "").slice(0, 15) }))} inputProps={{ inputMode: "numeric" }} />
                      <TextField label="Email" value={claimForm.customer_email} onChange={(event) => setClaimForm((prev) => ({ ...prev, customer_email: event.target.value }))} helperText="Optional" />
                      <Button variant="contained" sx={{ ...primaryButtonSx, minHeight: 46 }} disabled={claimSaving} onClick={savePrizeClaim}>{claimSaving ? "Saving..." : "Claim prize"}</Button>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        )}
        {phase === "calc" && (
          <>
            <Box sx={{ display: "flex", alignItems: "center", px: 1, pt: "max(10px, env(safe-area-inset-top))", pb: 0.5, flexShrink: 0 }}>
              {publicMode ? <SpinSale storeId={storeId} /> : (
                <button type="button" aria-label="Back to sales" onClick={() => navigate("/saleReport")} style={iconBtn}>
                  <ArrowBack sx={{ fontSize: 22, color: "#fff" }} />
                </button>
              )}
              <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 1, minWidth: 0, px: 1 }}>
                <Box component="img" src={cashiLogo} alt="Cashi" sx={{ height: 22, width: "auto", flexShrink: 0 }} />
                <Typography noWrap sx={{ color: "#d5dee8", fontSize: 14, fontWeight: 650 }}>{publicMode ? (storeName || "Sales Calculator") : "Sales Calculator"}</Typography>
              </Box>
              {publicMode ? <InstallSale /> : <SpinSale storeId={storeId} />}
            </Box>

            <Box sx={{ flexShrink: 0, px: 2, pt: 0.5, pb: 1.25 }}>
              <Typography sx={{ textAlign: "center", color: "#9aa3ab", fontSize: 34, lineHeight: 1, fontWeight: 500 }}>₹</Typography>
              <Typography sx={{ textAlign: "center", color: entry === "0" ? "#8b949e" : "#e8edf2", fontSize: 42, fontWeight: 500, letterSpacing: 0.5, lineHeight: 1.15 }}>
                {entry === "0" ? "00.00" : entryText(entry)}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.8, justifyContent: "center", flexWrap: "wrap", mt: 1.5, maxHeight: 72, overflow: "auto" }}>
                {lines.map((value, index) => (
                  <Box key={`${value}-${index}`} sx={{ background: "#fff", color: "#2c3136", borderRadius: 999, px: 1.3, py: 0.45, fontSize: 13, fontWeight: 700 }}>
                    {money(value)}
                  </Box>
                ))}
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1.5 }}>
                <Typography sx={{ color: "#fff", fontSize: 28, fontWeight: 700, lineHeight: 1 }}>₹ {money(committed)}</Typography>
                <Box sx={{ width: 36, height: 36, borderRadius: "50%", background: "#1a73e8", color: "#fff", display: "grid", placeItems: "center", fontWeight: 700, fontSize: 16, flexShrink: 0 }}>
                  {lines.length}
                </Box>
              </Box>
              {matched.length > 0 && (
                <Typography sx={{ color: "#93c5fd", fontSize: 13, pt: 1, lineHeight: 1.4 }}>
                  This amount qualifies for {matched.map((draw) => `${draw.coupons} coupon${draw.coupons === 1 ? "" : "s"} on ${draw.title}`).join(", ")}.
                </Typography>
              )}
            </Box>

            <Box sx={{ background: "#f4f6f8", display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gridTemplateRows: "repeat(5, minmax(0, 1fr))", flex: "1 1 auto", minHeight: 0, width: "100%" }}>
              {keys.map((key) => (
                <button key={key} type="button" onClick={() => press(key)} style={{ ...keyBtn, color: key === "+" ? "#1a8cff" : "#8d9bab", fontSize: key === "+" ? 32 : 26 }}>
                  {key}
                </button>
              ))}
              <button type="button" onClick={() => press("clear")} style={{ ...keyBtn, gridColumn: "span 2", fontSize: 16, letterSpacing: 1.2, fontWeight: 650 }}>
                CLEAR
              </button>
              <button type="button" aria-label="Delete" onClick={() => press("back")} style={keyBtn}>
                <BackspaceOutlined sx={{ fontSize: 22, color: "#8d9bab" }} />
              </button>
            </Box>

            <Box sx={{ background: "#2c3136", p: 1.25, flexShrink: 0 }}>
              <button type="button" onClick={openCustomer} style={registerBtn}>REGISTER SALE</button>
            </Box>
          </>
        )}

        {phase === "customer" && (
          <Box component="form" onSubmit={submit} sx={{ height: "100%", overflow: "auto", background: "#f3f6fa", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", px: 1, py: 1.2, background: "#2c3136" }}>
              <button type="button" aria-label="Back" onClick={() => setPhase("calc")} style={iconBtn}>
                <ArrowBack sx={{ fontSize: 22, color: "#fff" }} />
              </button>
              <Typography sx={{ flex: 1, textAlign: "center", color: "#fff", fontSize: 18, fontWeight: 650, pr: 4 }}>Register sale</Typography>
            </Box>
            <Box sx={{ p: 2, display: "grid", gap: 1.5 }}>
              <Typography sx={{ fontSize: 22, fontWeight: 750, color: "#0f172a" }}>₹ {money(amount)}</Typography>
              <TextField fullWidth sx={fieldSx} label="Customer mobile" value={form.customer_mobile} onChange={(event) => setForm({ ...form, customer_mobile: event.target.value.replace(/\D/g, "").slice(0, 10) })} required inputProps={{ inputMode: "numeric", maxLength: 10, autoComplete: "tel" }} />
              <TextField fullWidth sx={fieldSx} label="Customer name" value={form.customer_name} onChange={(event) => setForm({ ...form, customer_name: event.target.value })} required inputProps={{ autoComplete: "name" }} />
              <TextField fullWidth sx={fieldSx} label="Email (optional)" type="email" value={form.customer_email} onChange={(event) => setForm({ ...form, customer_email: event.target.value })} inputProps={{ inputMode: "email", autoComplete: "email" }} />
              {quote && Number(quote.loyalty_point) > 0 && (
                <Box sx={{ background: "#fff", borderRadius: 2, p: 1.5 }}>
                  <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: Number(quote.redeem_points) > 0 ? "pointer" : "default" }}>
                    <input
                      type="checkbox"
                      checked={usePoints && Number(quote.redeem_points) > 0}
                      disabled={Number(quote.redeem_points) < 1}
                      onChange={(event) => setUsePoints(event.target.checked)}
                      style={{ marginTop: 4 }}
                    />
                    <span>
                      <Typography sx={{ fontSize: 15, fontWeight: 750, color: "#0f172a" }}>
                        {Number(quote.redeem_points) > 0
                          ? `Use ${quote.redeem_points} points (₹${money(quote.redeem_value)})`
                          : `Balance ${quote.loyalty_point} points`}
                      </Typography>
                      <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.3, lineHeight: 1.4 }}>
                        {quote.source_text || `${quote.own_points || 0} at this store`}
                        {quote.club_title ? ` · ${quote.club_title}` : ""}
                      </Typography>
                      {quote.reason && Number(quote.redeem_points) < 1 ? (
                        <Typography sx={{ fontSize: 13, color: "#b45309", mt: 0.3 }}>{quote.reason}</Typography>
                      ) : null}
                    </span>
                  </label>
                </Box>
              )}
            </Box>
            <Box sx={{ mt: "auto", p: 1.5, background: "#2c3136" }}>
              <button type="submit" disabled={saving} style={registerBtn}>{saving ? "SAVING..." : "REGISTER SALE"}</button>
            </Box>
          </Box>
        )}

        {phase === "receipt" && (
          <Box sx={{ height: "100%", overflow: "auto", background: "#111", display: "flex", alignItems: "flex-start", justifyContent: "center", px: 2, py: { xs: 2, sm: 3 } }}>
            <Box sx={{ width: "100%", maxWidth: 420, background: "#fff", borderRadius: "22px", px: { xs: 2.5, sm: 3.5 }, py: { xs: 2.5, sm: 3 }, textAlign: "center", boxShadow: "0 18px 50px rgba(0,0,0,0.28)" }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative", mb: 1.5 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 750, color: "#1f2937" }}>Sale Registered</Typography>
                <button type="button" aria-label="Send on WhatsApp" onClick={shareWhatsApp} style={{ ...iconBtn, position: "absolute", right: -8, color: "#16a34a" }}>
                  <WhatsApp sx={{ fontSize: 26, color: "#16a34a" }} />
                </button>
              </Box>
              <CashiMark />
              <Typography sx={{ mt: 2, fontSize: 16, color: "#374151" }}>Customer Name: {result?.customer_name}</Typography>
              <Typography sx={{ mt: 1.2, fontSize: 16, color: "#374151" }}>Amount: ₹ {money(result?.amount)}</Typography>
              {Number(result?.points_redeemed) > 0 ? (
                <Typography sx={{ mt: 1.2, fontSize: 15, fontWeight: 700, color: "#b45309" }}>
                  Used {result.points_redeemed} points{result.redeem_value ? ` (₹${money(result.redeem_value)})` : ""}{result.redeem_text ? ` · ${result.redeem_text}` : ""}
                </Typography>
              ) : null}
              {Number(result?.points_earned) > 0 ? (
                <Typography sx={{ mt: 1.2, fontSize: 16, fontWeight: 700, color: "#15803d" }}>
                  Loyalty: +{result.points_earned} {Number(result.welcome_points) > 0 ? `(includes ${result.welcome_points} welcome)` : ""} · Balance {result.loyalty_point}
                </Typography>
              ) : Number(result?.loyalty_point) > 0 ? (
                <Typography sx={{ mt: 1.2, fontSize: 15, color: "#374151" }}>Loyalty balance: {result.loyalty_point} · Visits {result.total_visit}</Typography>
              ) : null}
              {(result?.unlocked || []).length > 0 ? (
                <Box sx={{ mt: 1.4, textAlign: "left" }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 750, color: "#0f172a", mb: 0.6 }}>Coupons unlocked</Typography>
                  {result.unlocked.map((item) => (
                    <Typography key={item.coupon_code || item.offer_id} sx={{ fontSize: 13.5, color: "#334155" }}>
                      {item.offer_title} · {item.coupon_code}
                    </Typography>
                  ))}
                </Box>
              ) : null}
              <Box sx={{ display: "flex", justifyContent: "center", my: 2.5 }}>
                <QRCodeCanvas value={receiptPayload(result, storeId, storeName)} size={220} includeMargin />
              </Box>
              <Typography sx={{ fontSize: 16, color: "#4b5563", mb: 2 }}>Ask Customer to Scan</Typography>
              <button type="button" onClick={() => setPayOpen((open) => !open)} style={linkBtn}>
                {result?.payment_type && result.payment_type !== "SALE" ? `Payment: ${result.payment_type}` : "Update Payment Status"}
              </button>
              {payOpen && (
                <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1.2, flexWrap: "wrap" }}>
                  {["CASH", "UPI", "CARD"].map((type) => (
                    <button key={type} type="button" onClick={() => updatePayment(type)} style={payChip}>{type}</button>
                  ))}
                </Box>
              )}
              {(result?.pending || []).length > 0 && (
                <button type="button" onClick={() => { setScanDraw(result.pending[0]); setPhase("scan"); }} style={{ ...linkBtn, marginTop: 14 }}>
                  Scan coupon {result.pending[0].coupon_index || 1} of {result.pending[0].coupon_total || result.pending.length}
                </button>
              )}
              <button type="button" onClick={async () => {
                const spin = await callPostApi("store-spin", { mod: "SPIN_ARM", data_arr: {
                  store_id: storeId,
                  customer_name: result?.customer_name || "",
                  customer_mobile: result?.customer_mobile || "",
                  customer_email: result?.customer_email || "",
                } });
                if (spin.status === "200" && spin.data?.armed === "1") {
                  setSpinNote(spin.data.headline ? `Spin is live: ${spin.data.headline}` : "Spin is live on the wheel");
                  toast.success("Spin is ready");
                } else toast.info(spin.data?.message || spin.status_message || "No spin wheel is running");
              }} style={{ ...linkBtn, marginTop: 14 }}>Now spin</button>
              {spinNote && <Typography sx={{ mt: 1, fontSize: 14, fontWeight: 700, color: "#15803d" }}>{spinNote}</Typography>}
              <button type="button" onClick={() => setPhase("stand")} style={{ ...linkBtn, marginTop: 14 }}>Send To QR Stand</button>
              <button type="button" onClick={resetSale} style={closeBtn}>CLOSE</button>
            </Box>
          </Box>
        )}

        {phase === "stand" && (
          <Box sx={{ height: "100%", background: "#111", color: "#fff", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", px: 2, textAlign: "center", gap: 1.5 }}>
            <Typography sx={{ fontSize: 22, fontWeight: 750 }}>{result?.customer_name}</Typography>
            <Typography sx={{ fontSize: 32, fontWeight: 800 }}>₹ {money(result?.amount)}</Typography>
            <Box sx={{ background: "#fff", p: 1.5, borderRadius: 2 }}>
              <QRCodeCanvas value={receiptPayload(result, storeId, storeName)} size={260} />
            </Box>
            <Typography sx={{ fontSize: 16, color: "#d1d5db" }}>Ask Customer to Scan</Typography>
            <button type="button" onClick={() => setPhase("receipt")} style={{ ...closeBtn, marginTop: 8 }}>CLOSE</button>
          </Box>
        )}

        {phase === "scan" && scanDraw && (
          <Box sx={{ height: "100%", overflow: "auto", background: "#111", display: "flex", flexDirection: "column" }}>
            <Box sx={{ display: "flex", alignItems: "center", px: 1, py: 1.2 }}>
              <button type="button" aria-label="Back" onClick={() => setPhase("receipt")} style={iconBtn}>
                <ArrowBack sx={{ fontSize: 22, color: "#fff" }} />
              </button>
              <Typography sx={{ flex: 1, textAlign: "center", color: "#fff", fontSize: 18, fontWeight: 700, pr: 4 }}>Issue Lucky Draw Coupons</Typography>
            </Box>
            <Box sx={{ px: 2, pb: 2, display: "grid", gap: 1.25 }}>
              <Typography sx={{ color: "#fff", fontSize: 18, fontWeight: 750, textAlign: "center" }}>Coupon {scanDraw.coupon_index || 1} of {scanDraw.coupon_total || 1}</Typography>
              <Typography sx={{ color: "#e5e7eb", fontSize: 15, textAlign: "center" }}>{scanDraw.title}</Typography>
              <Typography sx={{ color: "#9ca3af", fontSize: 13, textAlign: "center", lineHeight: 1.45 }}>
                Minimum ₹{money(scanDraw.min_sale_amount)}. Use the camera, a saved photo, or type coupon {scanDraw.coupon_index || 1}.
              </Typography>
              <QrScanner key={scanDraw.slot_id} onType={() => codeRef.current?.focus()} onCode={(value) => { setDraft(value); issuePending(value); }} />
              <TextField fullWidth sx={fieldSx} inputRef={codeRef} label={`Type coupon ${scanDraw.coupon_index || 1} code`} value={draft} onChange={(event) => setDraft(event.target.value.trim())} placeholder="Type the coupon code" />
            </Box>
            <Box sx={{ mt: "auto", p: 1.5 }}>
              <button type="button" disabled={saving} onClick={() => issuePending()} style={registerBtn}>{saving ? "ISSUING..." : `ISSUE COUPON ${scanDraw.coupon_index || 1}`}</button>
            </Box>
          </Box>
        )}

        {phase === "celebrate" && <Celebrate result={result} onClose={resetSale} onIssued={publicMode ? null : () => navigate(`/luckydraw/${result?.issued?.[0]?.draw_id}?view=issued`)} />}
      </Box>
    </Box>
  );
}

function scanChoice(busy, outlined) {
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 52,
    borderRadius: 1.5,
    border: outlined ? "1px solid #21a7ec" : 0,
    background: busy ? "#64748b" : (outlined ? "transparent" : "#21a7ec"),
    color: "#fff",
    fontWeight: 800,
    letterSpacing: 0.3,
    textAlign: "center",
    cursor: busy ? "default" : "pointer",
    px: 1,
  };
}

const typeChoice = {
  width: "100%",
  minHeight: 48,
  marginTop: 8,
  borderRadius: 8,
  border: "1px solid #334155",
  background: "#1f2937",
  color: "#fff",
  fontWeight: 800,
  letterSpacing: 0.4,
  cursor: "pointer",
};

const iconBtn = {
  width: 40,
  height: 40,
  border: 0,
  background: "transparent",
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};

const keyBtn = {
  border: "1px solid #e7ebef",
  background: "#f7f8fa",
  color: "#8d9bab",
  fontSize: 26,
  fontWeight: 500,
  cursor: "pointer",
  display: "grid",
  placeItems: "center",
};

const registerBtn = {
  width: "100%",
  minHeight: 52,
  border: 0,
  borderRadius: 2,
  background: "#fff",
  color: "#111",
  fontSize: 16,
  fontWeight: 800,
  letterSpacing: 0.6,
  cursor: "pointer",
};

const linkBtn = {
  display: "block",
  width: "100%",
  border: 0,
  background: "transparent",
  color: "#1a73e8",
  fontSize: 18,
  fontWeight: 750,
  cursor: "pointer",
  padding: "6px 0",
};

const payChip = {
  border: "1px solid #1a73e8",
  background: "#eff6ff",
  color: "#1d4ed8",
  borderRadius: 999,
  padding: "6px 14px",
  fontWeight: 700,
  cursor: "pointer",
};

const closeBtn = {
  marginTop: 22,
  minWidth: 140,
  minHeight: 44,
  border: 0,
  borderRadius: 10,
  background: "#2b2b2b",
  color: "#fff",
  fontSize: 15,
  fontWeight: 800,
  letterSpacing: 0.8,
  cursor: "pointer",
};

function receiptPayload(result, storeId, storeName) {
  return [
    "CASHI",
    storeName || storeId || "Store",
    `Customer: ${result?.customer_name || ""}`,
    `Mobile: ${result?.customer_mobile || ""}`,
    `Amount: ${money(result?.amount)}`,
    Number(result?.points_redeemed) ? `Used: ${result.points_redeemed}${result.redeem_text ? ` (${result.redeem_text})` : ""}` : "",
    Number(result?.points_earned) ? `Loyalty: +${result.points_earned}` : "",
    Number(result?.loyalty_point) ? `Balance: ${result.loyalty_point}` : "",
  ].filter(Boolean).join("\n");
}

function CashiMark() {
  return <Box component="img" src={cashiLogo} alt="Cashi" sx={{ height: 34, width: "auto" }} />;
}

function Celebrate({ result, onClose, onIssued }) {
  const colors = ["#fde047", "#fb7185", "#34d399", "#60a5fa", "#c084fc", "#fb923c", "#fff"];
  return (
    <Box sx={{ height: "100%", position: "relative", overflow: "hidden", background: "linear-gradient(145deg, #6d28d9 0%, #db2777 38%, #f59e0b 72%, #22c55e 100%)", backgroundSize: "180% 180%", animation: "cashi-shift 8s ease infinite", display: "flex", alignItems: "center", justifyContent: "center", px: 2 }}>
      {colors.concat(colors, colors, colors).map((color, index) => (
        <Box key={index} sx={{ position: "absolute", top: -20, left: `${(index * 13) % 100}%`, width: index % 3 === 0 ? 14 : 10, height: index % 2 === 0 ? 18 : 10, borderRadius: index % 2 ? "50%" : "2px", background: color, animation: `cashi-fall ${2.4 + (index % 5) * 0.35}s linear ${(index % 8) * 0.12}s infinite`, pointerEvents: "none" }} />
      ))}
      <Box sx={{ position: "relative", zIndex: 1, width: "100%", maxWidth: 460, textAlign: "center", color: "#fff", animation: "cashi-pop 0.55s ease" }}>
        <Box sx={{ width: 92, height: 92, mx: "auto", borderRadius: "50%", background: "rgba(255,255,255,0.18)", display: "grid", placeItems: "center", fontSize: 48, border: "3px solid rgba(255,255,255,0.7)" }}>✓</Box>
        <Typography sx={{ fontSize: { xs: 32, sm: 42 }, fontWeight: 850, lineHeight: 1.1, mt: 2 }}>Issued successfully</Typography>
        <Typography sx={{ fontSize: { xs: 16, sm: 18 }, mt: 1.2, opacity: 0.95 }}>{result?.customer_name} · ₹{money(result?.amount)}</Typography>
        <Box sx={{ mt: 2.5, display: "grid", gap: 1 }}>
          {(result?.issued || []).map((item) => (
            <Box key={item.coupon_code} sx={{ background: "rgba(255,255,255,0.16)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: 2.5, px: 2, py: 1.4 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 650 }}>{item.title}</Typography>
              <Typography sx={{ fontSize: { xs: 22, sm: 26 }, fontWeight: 850, wordBreak: "break-all", mt: 0.4 }}>{item.coupon_code}</Typography>
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "grid", gap: 1, mt: 3, maxWidth: 320, mx: "auto" }}>
          {onIssued && (result?.issued || []).length > 0 && <button type="button" onClick={onIssued} style={registerBtn}>ISSUED CUSTOMERS</button>}
          <button type="button" onClick={onClose} style={{ ...registerBtn, background: "transparent", color: "#fff", border: "1px solid rgba(255,255,255,0.7)" }}>NEW SALE</button>
        </Box>
      </Box>
      <style>{`
        @keyframes cashi-fall {
          0% { transform: translateY(-24px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(280deg); opacity: 0.2; }
        }
        @keyframes cashi-pop {
          0% { transform: scale(0.72); opacity: 0; }
          70% { transform: scale(1.05); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes cashi-shift {
          0% { background-position: 0% 40%; }
          50% { background-position: 100% 60%; }
          100% { background-position: 0% 40%; }
        }
      `}</style>
    </Box>
  );
}
